// Iteration 47 Phase 4 — Ledger KPI cards with drill-down modal.
// Drop-in for the Dashboard page. Consumes /api/ledger/kpis.
import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { DollarSign, TrendingUp, FileText, AlertTriangle, Info } from "lucide-react";
import LedgerDrilldown from "./LedgerDrilldown";

const money = (n) => (n || 0).toLocaleString("en-US", { style: "currency", currency: "USD" });

export default function LedgerKPICards() {
  const [data, setData] = useState(null);
  const [drilldown, setDrilldown] = useState(null);

  useEffect(() => { api.get("/ledger/kpis").then((r) => setData(r.data)); }, []);

  if (!data) return <div className="text-slate-500 text-sm">Loading financial reconciliation…</div>;

  const K = data.kpis;
  const cards = [
    { key: "revenue_paid", label: "Revenue (Paid)", value: money(K.revenue_paid.value), sub: `${K.revenue_paid.count} jobs · deduplicated`, icon: DollarSign, color: "emerald", metric: "revenue_paid", def: K.revenue_paid.definition },
    { key: "revenue_pending", label: "Pending Collection", value: money(K.revenue_pending.value), sub: `${K.revenue_pending.count} jobs · sent + approved`, icon: TrendingUp, color: "blue", metric: "revenue_pending", def: K.revenue_pending.definition },
    { key: "revenue_draft", label: "Draft (Not Yet Sent)", value: money(K.revenue_draft.value), sub: `${K.revenue_draft.count} jobs · draft`, icon: FileText, color: "slate", metric: "revenue_draft", def: K.revenue_draft.definition },
    { key: "gross_outstanding", label: "Gross Outstanding", value: money(K.gross_outstanding.value), sub: `Raw pending + draft (no dedup)`, icon: TrendingUp, color: "amber", metric: "gross_billed_pending", def: K.gross_outstanding.definition },
  ];

  const conflictCount = K.dual_invoice_conflicts.count;
  const dedupDelta = (K.gross_billed_paid.value || 0) - (K.revenue_paid.value || 0);

  return (
    <div className="space-y-4">
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="ledger-kpi-cards">
        {cards.map((c) => {
          const Icon = c.icon;
          return (
            <button
              key={c.key}
              onClick={() => setDrilldown({ metric: c.metric, title: c.label, definition: c.def })}
              className="text-left rounded-xl border border-slate-800 bg-[#0B1120] p-4 hover:border-slate-700 hover:bg-slate-900/40 transition-colors group"
              data-testid={`kpi-${c.key}`}
            >
              <div className="flex items-start justify-between mb-2">
                <div className={`w-8 h-8 rounded-lg bg-${c.color}-500/15 flex items-center justify-center border border-${c.color}-500/30`}>
                  <Icon className={`w-4 h-4 text-${c.color}-400`} />
                </div>
                <Info className="w-3 h-3 text-slate-600 group-hover:text-slate-400" />
              </div>
              <div className="text-2xl font-bold font-mono text-slate-100">{c.value}</div>
              <div className="text-xs text-slate-400 mt-1">{c.label}</div>
              <div className="text-[10px] text-slate-500 mt-1 truncate">{c.sub}</div>
            </button>
          );
        })}
      </div>

      {conflictCount > 0 && (
        <button
          onClick={() => setDrilldown({ metric: "dual_invoice_conflicts", title: "Dual Invoice Conflicts", definition: K.dual_invoice_conflicts.definition })}
          className="w-full text-left rounded-xl border border-amber-500/40 bg-amber-500/10 p-4 hover:bg-amber-500/15 transition-colors"
          data-testid="kpi-dual-conflicts"
        >
          <div className="flex items-center gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-400" />
            <div className="flex-1">
              <div className="font-semibold text-amber-200">
                {conflictCount} job{conflictCount !== 1 ? "s" : ""} with dual invoice conflict
              </div>
              <div className="text-xs text-amber-300/80 mt-0.5">
                Both insurance + body_shop invoices active. ${dedupDelta.toFixed(2)} would be double-counted as revenue.
              </div>
            </div>
            <div className="text-amber-300 text-xs">Review →</div>
          </div>
        </button>
      )}

      {data.profit_note && (
        <div className="text-[10px] text-slate-500 italic border-t border-slate-800 pt-2">
          {data.profit_note}
        </div>
      )}

      {drilldown && (
        <LedgerDrilldown {...drilldown} onClose={() => setDrilldown(null)} />
      )}
    </div>
  );
}
